import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Clock, Shield, ExternalLink, Landmark, Copy } from "lucide-react";

export default function MultiPago() {
  const { ordenId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/ordenes/${ordenId}/pagos-status`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenId]);

  if (loading) return <div className="min-h-screen bg-[#F9F9F8]"><Navbar /><div className="p-16 text-center text-zinc-500">Cargando…</div></div>;
  if (!data) return null;

  const pagado = data.all_paid;
  const bank = data.datos_transferencia;
  const copiar = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado"); };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10">
        <div className="overline text-zinc-500">Pago de tu pedido</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">{pagado ? "¡Pago recibido!" : "Completa tu pago"}</h1>

        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5 flex items-center justify-between">
          <div>
            <div className="overline text-zinc-500">Orden</div>
            <div className="font-mono font-black text-2xl mt-1" data-testid="pago-token">{data.token_seguridad}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-zinc-500">Total</div>
            <div className="font-display font-black text-2xl tracking-tight" data-testid="pago-total">${data.total.toFixed(2)} MXN</div>
          </div>
        </div>

        {pagado ? (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center" data-testid="pago-confirmado">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <div className="mt-3 font-display font-black text-2xl tracking-tighter">Los comercios ya están preparando tu pedido</div>
            {data.metodo_envio === "foraneo" && <p className="text-sm text-zinc-600 mt-2">Envío foráneo: recibirás tu paquete en un plazo de hasta 72 horas.</p>}
            <Link to="/cliente"><Button className="mt-4 rounded-sm bg-[#FF3366] hover:bg-[#E62958]" data-testid="multipago-view-orders">Seguir mi pedido</Button></Link>
          </div>
        ) : data.metodo_pago === "transferencia" ? (
          <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6" data-testid="datos-transferencia">
            <div className="flex items-center gap-2 text-zinc-900 font-display font-bold text-lg"><Landmark className="w-5 h-5" /> Transferencia bancaria</div>
            <p className="text-sm text-zinc-600 mt-1">Realiza la transferencia por el total exacto y usa el código <b>{data.token_seguridad}</b> como concepto. Confirmaremos tu pago y los comercios empezarán a preparar tu pedido.</p>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Beneficiario" value={bank.titular} onCopy={copiar} />
              <Row label="Banco" value={bank.banco} onCopy={copiar} />
              <Row label="CLABE" value={bank.clabe} mono onCopy={copiar} testId="clabe" />
              <Row label="Monto" value={`$${data.total.toFixed(2)} MXN`} onCopy={copiar} />
              <Row label="Concepto" value={data.token_seguridad} mono onCopy={copiar} />
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
              <Clock className="w-3.5 h-3.5" /> Tu pedido queda en espera hasta que confirmemos la transferencia.
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
            <p className="text-sm text-zinc-600">Tu pago con tarjeta aún no se ha completado.</p>
            {data.checkout_url && (
              <Button data-testid="retry-stripe" onClick={() => { window.location.href = data.checkout_url; }} className="mt-4 w-full h-11 rounded-sm bg-[#FF3366] hover:bg-[#E62958]">
                <ExternalLink className="w-4 h-4 mr-2" /> Pagar ${data.total.toFixed(2)} con tarjeta
              </Button>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500 justify-center">
          <Shield className="w-3 h-3" /> Pagos recibidos por {data.beneficiario}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, onCopy, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-zinc-100 pb-2">
      <span className="text-zinc-500">{label}</span>
      <span className={`flex items-center gap-2 font-medium ${mono ? "font-mono" : ""}`} data-testid={testId}>
        {value}
        <button type="button" onClick={() => onCopy(value)} className="text-zinc-400 hover:text-zinc-900"><Copy className="w-3.5 h-3.5" /></button>
      </span>
    </div>
  );
}
